import { useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { CardHeader, NoDataFound, stageColor, pctOfLeads } from "./Common";

const centerTextPlugin = {
  id: "funnelDonutCenterText",
  beforeDraw(chart) {
    const cfg = chart.config.options.plugins.centerText;
    if (!cfg) return;
    const { value, label } = cfg;
    const { ctx, chartArea } = chart;
    const x = (chartArea.left + chartArea.right) / 2;
    const y = (chartArea.top + chartArea.bottom) / 2;

    ctx.save();
    ctx.font = "600 20px sans-serif";
    ctx.fillStyle = "#1f2937";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText(String(value), x, y + 2);

    ctx.font = "400 10px sans-serif";
    ctx.fillStyle = "#9ca3af";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(label, x, y + 4);
    ctx.restore();
  },
};

export default function FunnelDonut({ stages }) {
  const [activeIndex, setActiveIndex] = useState(null);

  const segments = useMemo(() => {
    if (!stages?.length) return [];
    const leadsTotal = stages[0].total;
    if (!leadsTotal) return [];
    return stages.map((s, i) => ({
      ...s,
      color: stageColor(i),
      pct: pctOfLeads(s.total, leadsTotal),
    }));
  }, [stages]);

  const leadsTotal = segments.length ? segments[0].total : 0;
  const active = activeIndex !== null ? segments[activeIndex] : null;

  const chartData = {
    labels: segments.map((s) => s.stage_name),
    datasets: [
      {
        data: segments.map((s) => s.total),
        backgroundColor: segments.map((s) => s.color),
        borderColor: "#fff",
        borderWidth: 2,
        hoverOffset: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "70%",
    onHover: (_evt, elements) => setActiveIndex(elements.length ? elements[0].index : null),
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.label}: ${ctx.parsed} (${segments[ctx.dataIndex]?.pct ?? 0}% of leads)`,
        },
      },
      centerText: {
        value: active ? active.total : leadsTotal,
        label: active ? `${active.pct}% of leads` : "total leads",
      },
    },
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col">
      <CardHeader title="Stage distribution" subtitle="Each stage's addresses as a % of total leads" />

      {!segments.length ? (
        <NoDataFound label="No activity in this period" />
      ) : (
        <div className="flex items-center gap-5 flex-wrap">
          <div className="relative flex-shrink-0" style={{ width: 200, height: 200 }}>
            <Doughnut data={chartData} options={options} plugins={[centerTextPlugin]} />
          </div>

          <div className="flex flex-col gap-1.5 min-w-0 flex-1">
            {segments.map((s, i) => (
              <div key={s.stage_id} className={`flex items-center gap-2 text-xs rounded-md px-1.5 py-1 -mx-1.5 transition-colors ${activeIndex === i ? "bg-gray-50" : ""}`} onMouseEnter={() => setActiveIndex(i)} onMouseLeave={() => setActiveIndex(null)}>
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                <span className="text-gray-600 truncate flex-1" title={s.stage_name}>
                  {s.stage_name}
                </span>
                <span className="text-gray-400">{s.pct}% of leads</span>
                <span className="text-gray-700 font-medium w-8 text-right">{s.total}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
